/**
 * Forcing a session to expire, for local development and the UI tests.
 * --------------------------------------------------------------------
 * A real expiry cannot be reached locally: both stand-in adapters in
 * `keycloak.ts` resolve `updateToken` unconditionally, and a real one needs a
 * realm and a wait. So the stubs ask this module whether the next refresh
 * should fail instead.
 *
 * **Only the stubs read it.** `createRealKeycloak` never calls in here, so a
 * deployed build has no path to this switch even if the key were set by hand.
 */

export type ForcedExpiry =
  /** Refreshes succeed, which is the normal state. */
  | 'off'
  /** The next refresh fails, then this resets itself. Both overlay buttons use it. */
  | 'next'
  /**
   * Every refresh fails, and it survives a reload. Not offered in the overlay —
   * `tests/sessionExpiry.spec.ts` uses it to prove that many failing refreshes
   * still raise exactly one dialog, which a one-shot cannot show.
   */
  | 'always';

const STORAGE_KEY = 'mlp_dev_force_expiry';

const isForcedExpiry = (value: string | null): value is ForcedExpiry =>
  value === 'off' || value === 'next' || value === 'always';

/** localStorage, not session: 'always' has to survive the reload to be worth having. */
const read = (): ForcedExpiry => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isForcedExpiry(stored) ? stored : 'off';
  } catch {
    return 'off';
  }
};

export const getForcedExpiry = (): ForcedExpiry => read();

export const setForcedExpiry = (mode: ForcedExpiry): void => {
  try {
    if (mode === 'off') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore storage failures — dev only */
  }
};

/**
 * Answers the stub, and disarms a one-shot in the same step so a single
 * "expire on the next request" cannot turn into a tab that never recovers.
 */
export const consumeForcedExpiry = (): boolean => {
  const mode = read();
  if (mode === 'off') return false;
  if (mode === 'next') setForcedExpiry('off');
  return true;
};

/** What the stubs reject with, so the reason is legible in a console or a test. */
export const forcedExpiryError = (): Error =>
  new Error('Forced session expiry (development switch), not a real Keycloak failure');
